import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button';
import { ChevronLeftIcon } from '@navikt/aksel-icons';
import classes from './TenantDetailPageNew.module.css';
import buttonClasses from '../../../components/Button/Button.module.css';
import cn from 'classnames';
import { Routes } from '../../../enums/Routes';
import { useGetTenantDetailQuery } from '../../../hooks/queries/useGetTenantDetailQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { formatNorwegianDate } from '../../../utils/dateFormatters';
import { formatNumber } from '../../../utils/numberFormatters';
import { buildRoute } from '../../../utils/routeBuilder';

const ERROR_MESSAGE = 'Kunne ikke laste kundedata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';

type TabType = 'overview' | 'users' | 'licenses' | 'sms' | 'usage' | 'settings';

export const TenantDetailPageNew = (): ReactElement => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data: tenant, isLoading, error } = useGetTenantDetailQuery(Number(tenantId));
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const editSmsQuotaLink = buildRoute(Routes.SystemAdminEditSmsQuota, { tenantId: tenantId! });
  const editSeatsLink = buildRoute(Routes.SystemAdminEditSeats, { tenantId: tenantId! });
  const upgradeToPartnerLink = buildRoute(Routes.SystemAdminUpgradeTenantToPartner, { tenantId: tenantId! });

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Alert variant="danger" size="md">
          <Paragraph>{ERROR_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className={classes.container}>
        <Alert variant="warning" size="md">
          <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Oversikt' },
    { id: 'users' as const, label: 'Brukere' },
    { id: 'licenses' as const, label: 'Lisenser' },
    { id: 'sms' as const, label: 'SMS-kvote' },
    { id: 'usage' as const, label: 'Bruk' },
    { id: 'settings' as const, label: 'Innstillinger' },
  ];

  return (
    <div className={classes.container}>
      <Link to={Routes.SystemAdminTenants} className={classes.backLink}>
        <ChevronLeftIcon aria-hidden="true" fontSize="20px" />
        <span>Tilbake til kunder</span>
      </Link>

      <div className={classes.header}>
        <Heading as="h1">{tenant.name}</Heading>
        <Paragraph size="lg">
          Opprettet {formatNorwegianDate(tenant.createdAt)} · Eier: <strong>{tenant.partnerName || 'Trustpulse'}</strong>
        </Paragraph>
      </div>

      <div className={classes.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={classes.tabContent}>
        {activeTab === 'overview' && (
          <div className={classes.statsGrid}>
            <ContainerCard>
              <ContainerCard.Title>Antall team</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{tenant.teams.length}</div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Antall brukere</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{tenant.users.length}</div>
                <div className={classes.statMeta}>
                  {tenant.users.filter(u => !u.isPending).length} aktive
                </div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>SMS brukt</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{formatNumber(tenant.smsStats.smsUsed)}</div>
                <div className={classes.statMeta}>
                  av {tenant.smsStats.smsQuota === null ? 'Ubegrenset' : formatNumber(tenant.smsStats.smsQuota)}
                </div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Lisenser brukt</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{formatNumber(tenant.seatsStats.seatsUsed)}</div>
                <div className={classes.statMeta}>
                  av {tenant.seatsStats.availableSeats === null ? 'Ubegrenset' : formatNumber(tenant.seatsStats.availableSeats)}
                </div>
              </ContainerCard.Body>
            </ContainerCard>
          </div>
        )}

        {activeTab === 'users' && (
          <ContainerCard>
            <ContainerCard.Title>Brukere</ContainerCard.Title>
            <ContainerCard.Body>
              {tenant.users.length === 0 ? (
                <Paragraph>Ingen brukere funnet.</Paragraph>
              ) : (
                <div className={classes.tableWrapper}>
                  <table className={classes.dataTable}>
                    <thead>
                      <tr>
                        <th>E-post</th>
                        <th>Rolle</th>
                        <th>Status</th>
                        <th>Opprettet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenant.users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <Link
                              to={Routes.SystemAdminUserDetail.replace(':userId', String(user.id))}
                              className={classes.userLink}
                            >
                              <strong>{user.email}</strong>
                            </Link>
                          </td>
                          <td>{user.role}</td>
                          <td>{user.isPending ? 'Venter' : 'Aktiv'}</td>
                          <td>{formatNorwegianDate(user.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'licenses' && (
          <ContainerCard>
            <ContainerCard.Title>Lisensstyring</ContainerCard.Title>
            <ContainerCard.Body>
              <div className={classes.infoGrid}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Antall lisenser:</span>
                  <span className={classes.infoValue}>
                    {tenant.seatsStats.availableSeats === null
                      ? 'Ubegrenset'
                      : formatNumber(tenant.seatsStats.availableSeats)}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Brukt:</span>
                  <span className={classes.infoValue}>{formatNumber(tenant.seatsStats.seatsUsed)}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Tilgjengelig:</span>
                  <span className={classes.infoValue}>
                    {tenant.seatsStats.availableSeats === null
                      ? 'Ubegrenset'
                      : formatNumber(tenant.seatsStats.availableSeats - tenant.seatsStats.seatsUsed)}
                  </span>
                </div>
              </div>
              <div className={classes.actions}>
                <Link to={editSeatsLink}>
                  <Button variant="secondary" size="medium">
                    Rediger lisenser
                  </Button>
                </Link>
              </div>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'sms' && (
          <ContainerCard>
            <ContainerCard.Title>SMS-kvote</ContainerCard.Title>
            <ContainerCard.Body>
              <div className={classes.infoGrid}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Total kvote:</span>
                  <span className={classes.infoValue}>
                    {tenant.smsStats.smsQuota === null
                      ? 'Ubegrenset'
                      : formatNumber(tenant.smsStats.smsQuota)}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Brukt:</span>
                  <span className={classes.infoValue}>{formatNumber(tenant.smsStats.smsUsed)}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Gjenstående:</span>
                  <span className={classes.infoValue}>
                    {tenant.smsStats.smsQuota === null
                      ? 'Ubegrenset'
                      : formatNumber(tenant.smsStats.smsQuota - tenant.smsStats.smsUsed)}
                  </span>
                </div>
                {tenant.smsStats.smsQuotaResetAt && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Neste reset:</span>
                    <span className={classes.infoValue}>{formatNorwegianDate(tenant.smsStats.smsQuotaResetAt)}</span>
                  </div>
                )}
              </div>
              <div className={classes.actions}>
                <Link to={editSmsQuotaLink}>
                  <Button variant="secondary" size="medium">
                    Rediger SMS-kvote
                  </Button>
                </Link>
              </div>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'usage' && (
          <ContainerCard>
            <ContainerCard.Title>Brukshistorikk</ContainerCard.Title>
            <ContainerCard.Body>
              <Paragraph>
                Volum og bruksstatistikk over tid vil vises her når API-endepunkter er tilgjengelige.
              </Paragraph>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'settings' && (
          <div className={classes.settingsContainer}>
            {!tenant.isPartner && (
              <ContainerCard>
                <ContainerCard.Title>Partner-status</ContainerCard.Title>
                <ContainerCard.Body>
                  <Paragraph>
                    Denne kunden er ikke en partner. Oppgrader kunden til partner for å la dem videreselge Trustpulse til sine egne kunder.
                  </Paragraph>
                  <div className={classes.actionButtons}>
                    <Link
                      to={upgradeToPartnerLink}
                      className={cn(buttonClasses.button, buttonClasses.primary, buttonClasses.medium)}
                    >
                      Oppgrader til partner
                    </Link>
                  </div>
                </ContainerCard.Body>
              </ContainerCard>
            )}

            {tenant.isPartner && (
              <ContainerCard>
                <ContainerCard.Title>Partner-status</ContainerCard.Title>
                <ContainerCard.Body>
                  <Alert variant="success" size="sm">
                    <Paragraph>Denne kunden er en partner.</Paragraph>
                  </Alert>
                  {tenant.partnerId && (
                    <div className={classes.actionButtons}>
                      <Link to={buildRoute(Routes.SystemAdminPartnerDetail, { partnerId: tenant.partnerId.toString() })}>
                        <Button variant="secondary">Vis partner-detaljer</Button>
                      </Link>
                    </div>
                  )}
                </ContainerCard.Body>
              </ContainerCard>
            )}

            <ContainerCard>
              <ContainerCard.Title>Moduler</ContainerCard.Title>
              <ContainerCard.Body>
                <Paragraph>
                  Administrer hvilke moduler og features kunden har tilgang til.
                </Paragraph>
                <div className={classes.actionButtons}>
                  <Link
                    to={buildRoute(Routes.SystemAdminTenantFeatures, { tenantId: tenant.id.toString() })}
                    className={cn(buttonClasses.button, buttonClasses.primary, buttonClasses.medium)}
                  >
                    Administrer moduler
                  </Link>
                </div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Innstillinger</ContainerCard.Title>
              <ContainerCard.Body>
                <Paragraph>
                  Full administrativ tilgang til kundeinnstillinger vil være tilgjengelig her.
                </Paragraph>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Faresone</ContainerCard.Title>
              <ContainerCard.Body>
                <div className={classes.dangerZone}>
                  <Paragraph size="sm">
                    Kritiske handlinger som sletting av kunde vil være tilgjengelig her.
                  </Paragraph>
                </div>
              </ContainerCard.Body>
            </ContainerCard>
          </div>
        )}
      </div>
    </div>
  );
};
